import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useModal } from 'components/modals/ModalContext';
import { TimelineItemType } from 'types/TimelineTypes';
import { UserType } from '../../views/users/UserTypes';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesDown, faAnglesUp } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { EmailMessage } from '../../views/email/EmailTypes';
import EmailForm from '../../views/email/EmailForm';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import EmailMessageViewer from '../../views/email/EmailMessageViewer';
import '../../style/scss/tooltip.scss';

interface TimelineEmailProps {
    placement: 'summary' | 'page';
    item: TimelineItemType;
    email: EmailMessage;
    timeline_page: string;
    timeline_page_id: number;
    sentBy?: UserType | null;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const TimelineEmail: React.FC<TimelineEmailProps> = ({ 
    placement, item, email, timeline_page_id, timeline_page, sentBy, onSubmitSuccess
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const [isEditing, setIsEditing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isExpandable, setIsExpandable] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const helperTextRef = useRef<HTMLDivElement>(null);
    
    // Format the sent date
    const emailDate = formatTimelineDate(item.ordering_date, false);

    // Get the sent by name
    const sentByName = sentBy?.full_name ?? email.sent_by_full_name ?? null;

    // Get the email title 
    const title = email.template_name || email.subject || t('timeline.email.general.email_fallback_title');

    // Generate the plain body
    const plainBody = useMemo(() => {
        const html = email.body || '';
        const element = document.createElement('div');

        element.innerHTML = html
            .replace(/<\/p>/gi, '</p> ')
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<\/div>/gi, '</div> ')
            .replace(/<\/li>/gi, '</li> ');

        return (element.textContent || element.innerText || '').replace(/\s+/g, ' ').trim();
    }, [email.body]);

    // Calculate the height of the plain text
    useEffect(() => {
        const timer = setTimeout(() => {
            if (textRef.current && helperTextRef.current) {
                const visibleTextHeight = textRef.current.offsetHeight;
                const helperTextHeight = helperTextRef.current.offsetHeight;

                setIsExpandable(helperTextHeight > visibleTextHeight);
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [plainBody]);

    // Open the email modal
    const handleOpenEmailViewer = () => {
        initializeModal(
            <EmailMessageViewer
                email={email}
                mode="modal"
                sentByName={sentByName}
                sentDate={emailDate}
            />,
            {
                modalSize: 'medium',
                title,
            }
        );
    };

    // Expand the message of the email
    const toggleIsExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Delete the email message in the database
    // const deleteEmail = async () => {
    //     try {
    //         // Call the delete email function on the backend
    //         const response = await saveData({ 
    //             apiUrl: `delete_email/${email.id}`,
    //             method: 'patch',
    //             data: {},
    //             source: axios.CancelToken.source()
    //         })

    //         // Handle successful response
    //         if (response && response.status === 200) {
    //             // Show success alert
    //             setFloatingAlert({ 'type': 'success' });

    //             // Update the timeline items state
    //             onSubmitSuccess?.({
    //                 ...response.data,
    //                 timeline_object_type: 'email'
    //             });
    //         }
    //     } catch (error) {
    //         setFloatingAlert({ type: 'danger', message: 'timeline.email.alert.delete_email_failed_message' })
    //     }
    // };

    return (
        <div className='timeline-item email'>
            {placement === 'page' && (
                <>
                    <div className='marker-container'>
                        <div className="big-marker">
                            <FontAwesomeIcon icon={faEnvelope as IconProp} className='note-icon fa-fw' />
                        </div>
                    </div>
                    <div className={`email-content ${isEditing ? 'editing' : ''}`}>
                        {isEditing ? (
                            <EmailForm
                                placement='inline'
                                timeline_page={timeline_page}
                                timeline_page_id={item.timeline_page_id}
                                emailId={item.timeline_object_id}
                                divisionId={null}
                                closeForm={() => setIsEditing(false)}
                                onSubmitSuccess={onSubmitSuccess}
                            />
                        ) : (
                            <>
                                <div className='email-header'>
                                    {email.template_name && (
                                        <div className='email-subject'>
                                            {email.template_name}
                                        </div>
                                    )}
                                    <div className='email-message'>
                                        <div className='left-items' style={{ position: 'relative' }}>
                                            {!isExpanded ? (
                                                <>
                                                    <EmailMessageViewer
                                                        email={email}
                                                        mode="inline"
                                                        sentByName={sentByName}
                                                        sentDate={emailDate}
                                                        collapsed
                                                    />
                                                    <p
                                                        className="page-email-body-preview"
                                                        ref={textRef}
                                                    >
                                                        {plainBody}
                                                    </p>
                                                    <p
                                                        className="page-email-body-helper"
                                                        ref={helperTextRef}
                                                    >
                                                        {plainBody}
                                                    </p>
                                                </>
                                            ) : (
                                                <EmailMessageViewer
                                                    email={email}
                                                    mode="inline"
                                                    sentByName={sentByName}
                                                    sentDate={emailDate}
                                                />
                                            )}

                                        </div>
                                    </div>
                                    <div className='email-info'>
                                        <div className='left-items'>
                                            <span>
                                                {emailDate}
                                            </span>
                                            {sentBy && sentBy.full_name && (
                                                <>
                                                    <span style={{ margin: '0 5px' }}>•</span>
                                                    <span>{sentBy.full_name}</span>
                                                </>
                                            )}
                                        </div>
                                        <div className='right-items'>
                                            {isExpandable && !isEditing &&
                                                <div className='tooltip-icon expand-icon' onClick={toggleIsExpanded}>
                                                    <FontAwesomeIcon icon={isExpanded ? faAnglesUp : faAnglesDown} />
                                                    <span className="tooltip">{isExpanded ? t('general.shrink_label') : t('general.expand_label')}</span>
                                                </div>
                                            }
                                            {/* <div 
                                                className='tooltip-icon delete-icon'
                                                onClick={deleteEmail}
                                            >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                    <span className="tooltip">{t('general.delete')}</span>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
            {placement === 'summary' && (
                <div className={`email-content ${isEditing ? 'editing' : ''}`}>
                    <div className='marker-container'>
                        <div className="medium-marker email">
                            <FontAwesomeIcon icon={faEnvelope as IconProp} className='note-icon fa-fw' />
                        </div>
                    </div>
                    <div>
                        <div 
                            className='email-subject'
                            onClick={handleOpenEmailViewer}
                        >
                            {title}
                        </div>
                        <div className='email-info'>
                            <div className='left-items'>
                                <span>
                                    {emailDate}
                                </span>
                                {sentBy && sentBy.full_name && (
                                    <>
                                        <span style={{ margin: '0 5px' }}>•</span>
                                        <span>{sentBy.full_name}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimelineEmail;